import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Paper,
  Grid,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  AccountTree as AccountTreeIcon,
  Apps as AppsIcon,
  Computer as ComputerIcon,
  Storage as StorageIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
  Info as InfoIcon,
  FilterList as FilterListIcon,
  Person as PersonIcon,
  VpnKey as VpnKeyIcon,
  Cloud as CloudIcon,
  Storage as DatabaseIcon,
  Computer as ServerIcon
} from '@mui/icons-material';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  ReactFlowProvider,
  MiniMap,
  NodeTypes,
  EdgeTypes,
  MarkerType,
  Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import { pamAPI } from '../services/api';
import { PAMAccount, PAMDependency } from '../types';
import { useNotification } from '../contexts/NotificationContext';

// Custom Node Components
const ApplicationNode = ({ data }: { data: any }) => (
  <Card sx={{ minWidth: 200, border: '2px solid #1976d2' }}>
    <CardContent sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <AppsIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="subtitle2" fontWeight="bold" color="primary">
          Application
        </Typography>
      </Box>
      <Typography variant="body2" fontWeight="medium">
        {data.name}
      </Typography>
    </CardContent>
  </Card>
);

const ServerNode = ({ data }: { data: any }) => (
  <Card sx={{ minWidth: 180, border: '2px solid #ed6c02' }}>
    <CardContent sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <ServerIcon color="warning" sx={{ mr: 1 }} />
        <Typography variant="subtitle2" fontWeight="bold" color="warning.main">
          Server
        </Typography>
      </Box>
      <Typography variant="body2" fontWeight="medium">
        {data.name}
      </Typography>
    </CardContent>
  </Card>
);

const DatabaseNode = ({ data }: { data: any }) => (
  <Card sx={{ minWidth: 180, border: '2px solid #2e7d32' }}>
    <CardContent sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <DatabaseIcon color="success" sx={{ mr: 1 }} />
        <Typography variant="subtitle2" fontWeight="bold" color="success.main">
          Database
        </Typography>
      </Box>
      <Typography variant="body2" fontWeight="medium">
        {data.name}
      </Typography>
    </CardContent>
  </Card>
);

const AccountNode = ({ data }: { data: any }) => {
  const getAccountIcon = (accountType: string) => {
    switch (accountType) {
      case 'service': return <PersonIcon />;
      case 'database': return <StorageIcon />;
      case 'os': return <ComputerIcon />;
      case 'api': return <VpnKeyIcon />;
      case 'cloud_iam': return <CloudIcon />;
      default: return <SecurityIcon />;
    }
  };

  const getAccountColor = (accountType: string) => {
    switch (accountType) {
      case 'service': return '#9c27b0';
      case 'database': return '#2e7d32';
      case 'os': return '#ed6c02';
      case 'api': return '#1976d2';
      case 'cloud_iam': return '#0288d1';
      default: return '#666';
    }
  };

  const getRiskColor = (riskScore: number) => {
    if (riskScore < 4) return '#4caf50';
    if (riskScore < 7) return '#ff9800';
    return '#f44336';
  };

  return (
    <Card sx={{ minWidth: 200, border: `2px solid ${getAccountColor(data.account_type)}` }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box sx={{ color: getAccountColor(data.account_type), mr: 1 }}>
            {getAccountIcon(data.account_type)}
          </Box>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ color: getAccountColor(data.account_type) }}>
            {data.account_type.toUpperCase()}
          </Typography>
        </Box>
        <Typography variant="body2" fontWeight="medium">
          {data.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {data.classification}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
          <Chip 
            label={`Risk: ${data.risk_score}`} 
            size="small" 
            sx={{ 
              backgroundColor: getRiskColor(data.risk_score),
              color: 'white',
              fontSize: '0.7rem',
              height: 20
            }}
          />
          <Chip 
            label={data.status} 
            size="small" 
            variant="outlined"
            sx={{ ml: 1, fontSize: '0.7rem', height: 20 }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

// Node types
const nodeTypes: NodeTypes = {
  application: ApplicationNode,
  server: ServerNode,
  database: DatabaseNode,
  account: AccountNode,
};

// Edge types - using default edges with labels
const edgeTypes: EdgeTypes = {};

const DependencyGraph: React.FC = () => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const muiTheme = useMuiTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  
  // State management
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<PAMAccount[]>([]);
  const [usageLogs, setUsageLogs] = useState<Array<{ account_id?: string; used_by_name?: string; usage_type?: string }>>([]);
  const [dependencies, setDependencies] = useState<PAMDependency[]>([]);
  const [selectedApplication, setSelectedApplication] = useState<string>('all');
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [nodeDetailsOpen, setNodeDetailsOpen] = useState(false);
  
  // React Flow state
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Load data
  useEffect(() => {
    loadDependencyData();
  }, []);

  // Regenerate graph when application filter changes
  useEffect(() => {
    generateGraph();
  }, [selectedApplication, accounts, dependencies]);

  const loadDependencyData = async () => {
    try {
      setLoading(true);
      
      const [accountsResponse, dependenciesResponse] = await Promise.all([
        pamAPI.getAccounts(),
        pamAPI.getDependencies()
      ]);

      if (accountsResponse.data.success) {
        setAccounts(accountsResponse.data.data);
      }
      
      if (dependenciesResponse.data.success) {
        setDependencies(dependenciesResponse.data.data);
      }
      try {
        const usageResponse = await pamAPI.getUsageLogs();
        if (usageResponse.data.success) {
          setUsageLogs(usageResponse.data.data || []);
        }
      } catch {
        setUsageLogs([]);
      }
      
    } catch (error) {
      console.error('Error loading dependency data:', error);
      showError('Failed to load dependency data');
    } finally {
      setLoading(false);
    }
  };

  // Generate unique applications
  const applications = useMemo(() => {
    const appMap = new Map();
    accounts.forEach(account => {
      if (!appMap.has(account.application_id)) {
        appMap.set(account.application_id, {
          id: account.application_id,
          name: account.application_name
        });
      }
    });
    return Array.from(appMap.values());
  }, [accounts]);

  // Generate unique servers
  const servers = useMemo(() => {
    const serverMap = new Map();
    accounts.forEach(account => {
      if (account.server_id && account.server_name && !serverMap.has(account.server_id)) {
        serverMap.set(account.server_id, {
          id: account.server_id,
          name: account.server_name
        });
      }
    });
    return Array.from(serverMap.values());
  }, [accounts]);

  // Generate unique databases
  const databases = useMemo(() => {
    const dbMap = new Map();
    accounts.forEach(account => {
      if (account.database_id && account.database_name && !dbMap.has(account.database_id)) {
        dbMap.set(account.database_id, {
          id: account.database_id,
          name: account.database_name
        });
      }
    });
    return Array.from(dbMap.values());
  }, [accounts]);

  // Generate graph nodes and edges
  const generateGraph = useCallback(() => {
    const newNodes: Node[] = [];
    const newEdges: Edge[] = [];
    let nodeId = 0;
    let edgeId = 0;

    // Filter data based on selected application
    const filteredAccounts = selectedApplication === 'all' 
      ? accounts 
      : accounts.filter(acc => acc.application_id === selectedApplication);

    const filteredDependencies = selectedApplication === 'all'
      ? dependencies
      : dependencies.filter(dep => dep.application_id === selectedApplication);

    // Create application nodes
    const appNodes = new Map();
    filteredAccounts.forEach(account => {
      if (!appNodes.has(account.application_id)) {
        const appNode: Node = {
          id: `app-${account.application_id}`,
          type: 'application',
          position: { x: 100, y: 100 + (appNodes.size * 200) },
          data: {
            id: account.application_id,
            name: account.application_name,
            type: 'application'
          }
        };
        newNodes.push(appNode);
        appNodes.set(account.application_id, appNode);
      }
    });

    // Create server nodes
    const serverNodes = new Map();
    filteredAccounts.forEach(account => {
      if (account.server_id && account.server_name && !serverNodes.has(account.server_id)) {
        const serverNode: Node = {
          id: `server-${account.server_id}`,
          type: 'server',
          position: { x: 400, y: 100 + (serverNodes.size * 200) },
          data: {
            id: account.server_id,
            name: account.server_name,
            type: 'server'
          }
        };
        newNodes.push(serverNode);
        serverNodes.set(account.server_id, serverNode);
      }
    });

    // Create database nodes
    const dbNodes = new Map();
    filteredAccounts.forEach(account => {
      if (account.database_id && account.database_name && !dbNodes.has(account.database_id)) {
        const dbNode: Node = {
          id: `db-${account.database_id}`,
          type: 'database',
          position: { x: 700, y: 100 + (dbNodes.size * 200) },
          data: {
            id: account.database_id,
            name: account.database_name,
            type: 'database'
          }
        };
        newNodes.push(dbNode);
        dbNodes.set(account.database_id, dbNode);
      }
    });

    // Create account nodes
    const accountNodes = new Map();
    filteredAccounts.forEach(account => {
      const accountNode: Node = {
        id: `account-${account.id}`,
        type: 'account',
        position: { x: 1000, y: 100 + (accountNodes.size * 200) },
        data: {
          id: account.id,
          name: account.name,
          account_type: account.account_type,
          classification: account.classification,
          risk_score: account.risk_score,
          status: account.status,
          owner_name: account.owner_name,
          owner_email: account.owner_email,
          type: 'account'
        }
      };
      newNodes.push(accountNode);
      accountNodes.set(account.id, accountNode);
    });

    // Create edges
    filteredAccounts.forEach(account => {
      // App -> Server edge
      if (account.server_id && appNodes.has(account.application_id) && serverNodes.has(account.server_id)) {
        const edge: Edge = {
          id: `edge-${edgeId++}`,
          source: `app-${account.application_id}`,
          target: `server-${account.server_id}`,
          type: 'smoothstep',
          animated: true,
          style: { stroke: '#1976d2', strokeWidth: 2 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#1976d2',
          },
          label: 'uses'
        };
        newEdges.push(edge);
      }

      // Server -> Database edge
      if (account.database_id && account.server_id && serverNodes.has(account.server_id) && dbNodes.has(account.database_id)) {
        const edge: Edge = {
          id: `edge-${edgeId++}`,
          source: `server-${account.server_id}`,
          target: `db-${account.database_id}`,
          type: 'smoothstep',
          animated: true,
          style: { stroke: '#ed6c02', strokeWidth: 2 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#ed6c02',
          },
          label: 'connects to'
        };
        newEdges.push(edge);
      }

      // Database -> Account edge
      if (account.database_id && accountNodes.has(account.id) && dbNodes.has(account.database_id)) {
        const edge: Edge = {
          id: `edge-${edgeId++}`,
          source: `db-${account.database_id}`,
          target: `account-${account.id}`,
          type: 'smoothstep',
          animated: true,
          style: { stroke: '#2e7d32', strokeWidth: 2 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#2e7d32',
          },
          label: 'accessed by'
        };
        newEdges.push(edge);
      }

      // Server -> Account edge (for OS accounts)
      if (account.account_type === 'os' && account.server_id && accountNodes.has(account.id) && serverNodes.has(account.server_id)) {
        const edge: Edge = {
          id: `edge-${edgeId++}`,
          source: `server-${account.server_id}`,
          target: `account-${account.id}`,
          type: 'smoothstep',
          animated: true,
          style: { stroke: '#9c27b0', strokeWidth: 2 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#9c27b0',
          },
          label: 'managed by'
        };
        newEdges.push(edge);
      }

      // App -> Account edge (for service accounts)
      if (account.account_type === 'service' && appNodes.has(account.application_id) && accountNodes.has(account.id)) {
        const edge: Edge = {
          id: `edge-${edgeId++}`,
          source: `app-${account.application_id}`,
          target: `account-${account.id}`,
          type: 'smoothstep',
          animated: true,
          style: { stroke: '#1976d2', strokeWidth: 2 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#1976d2',
          },
          label: 'uses'
        };
        newEdges.push(edge);
      }
    });

    setNodes(newNodes);
    setEdges(newEdges);
  }, [selectedApplication, accounts, dependencies, setNodes, setEdges]);

  // Handle node click
  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
    setNodeDetailsOpen(true);
  }, []);

  // Handle edge connection
  const onConnect = useCallback((params: Connection) => {
    setEdges((eds) => addEdge(params, eds));
  }, [setEdges]);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading Dependency Graph...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Dependency Graph
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Visualize application → server → database → account relationships
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Application</InputLabel>
            <Select
              value={selectedApplication}
              onChange={(e) => setSelectedApplication(e.target.value)}
              label="Filter by Application"
            >
              <MenuItem value="all">All Applications</MenuItem>
              {applications.map((app) => (
                <MenuItem key={app.id} value={app.id}>
                  {app.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadDependencyData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <AppsIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="primary">
                {applications.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Applications
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ServerIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="warning.main">
                {servers.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Servers
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <DatabaseIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="success.main">
                {databases.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Databases
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <SecurityIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="info.main">
                {accounts.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Privileged Accounts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Graph Visualization */}
      <Box sx={{ flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            attributionPosition="bottom-left"
            style={{ background: isDark ? '#0f1419' : '#fafafa' }}
          >
            <Controls />
            <MiniMap 
              nodeColor={(node) => {
                switch (node.type) {
                  case 'application': return '#1976d2';
                  case 'server': return '#ed6c02';
                  case 'database': return '#2e7d32';
                  case 'account': return '#9c27b0';
                  default: return '#666';
                }
              }}
              nodeStrokeWidth={3}
              zoomable
              pannable
              style={{ background: isDark ? '#1a2332' : '#fff' }}
            />
            <Background color={isDark ? '#2d3748' : '#d0d0d0'} gap={20} />
          </ReactFlow>
        </ReactFlowProvider>
      </Box>

      {/* Legend */}
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Legend
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 20, backgroundColor: '#1976d2', borderRadius: 1 }} />
              <Typography variant="body2">Applications</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 20, backgroundColor: '#ed6c02', borderRadius: 1 }} />
              <Typography variant="body2">Servers</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 20, backgroundColor: '#2e7d32', borderRadius: 1 }} />
              <Typography variant="body2">Databases</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 20, backgroundColor: '#9c27b0', borderRadius: 1 }} />
              <Typography variant="body2">Privileged Accounts</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Node Details Dialog */}
      <Dialog 
        open={nodeDetailsOpen} 
        onClose={() => setNodeDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedNode?.data?.type?.toUpperCase()} Details
        </DialogTitle>
        <DialogContent>
          {selectedNode && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedNode.data.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ textTransform: 'none' }}
                    onClick={() => navigate(selectedNode.data.type === 'account' ? '/pam-dashboard' : '/applications')}
                  >
                    {selectedNode.data.type === 'account' ? 'Open this account' : 'Open applications'}
                  </Button>
                </Grid>
                
                {selectedNode.data.type === 'account' && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Jobs that use this account</Typography>
                      <Typography variant="body2">
                        {usageLogs.filter((log) => log.account_id === selectedNode.data.id).length === 0
                          ? 'No jobs are recorded for this account.'
                          : usageLogs
                              .filter((log) => log.account_id === selectedNode.data.id)
                              .map((log) => {
                                const work = (log as { details?: string }).details || log.usage_type || 'Job';
                                return log.used_by_name ? `${work} (${log.used_by_name})` : work;
                              })
                              .join(', ')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Account Type</Typography>
                      <Typography variant="body2">{selectedNode.data.account_type}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Classification</Typography>
                      <Typography variant="body2">{selectedNode.data.classification}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Risk Score</Typography>
                      <Chip 
                        label={selectedNode.data.risk_score} 
                        size="small" 
                        color={selectedNode.data.risk_score >= 7 ? 'error' : selectedNode.data.risk_score >= 4 ? 'warning' : 'success'}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                      <Chip 
                        label={selectedNode.data.status} 
                        size="small" 
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Owner</Typography>
                      <Typography variant="body2">{selectedNode.data.owner_name}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Owner Email</Typography>
                      <Typography variant="body2">{selectedNode.data.owner_email}</Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNodeDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DependencyGraph;
